/* eslint-disable react/no-unescaped-entities */
/**
 * /verifier-un-avocat, /verifier-un-expert-comptable, /verifier-un-notaire,
 * /verifier-un-commissaire-de-justice, /verifier-une-societe, /verifier-un-professionnel
 *
 * PILIER 4 SEO — La vérification d'un professionnel est le différentiateur
 * unique de CONFILEX vs réseaux de spécialistes classiques.
 */
import { Link } from "react-router-dom";
import {
  ShieldCheck, BadgeCheck, Search, FileCheck2, Building2, Scale,
  ArrowRight, Compass, ScanLine, Users, Lock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Seo, { ldBreadcrumb, ldFAQ } from "@/components/Seo";


export const VERIFY_PRO_PAGES = {
  "verifier-un-avocat": {
    label: "avocat",
    plural: "avocats",
    h1: "Vérifier un avocat avant de lui confier votre dossier",
    seo_title: "Vérifier un avocat — Contrôle institutionnel · CONFILEX",
    seo_description: "Avant de confier votre dossier à un avocat, vérifiez son inscription au Barreau, sa spécialisation et son expérience. Contrôle documentaire CONFILEX.",
    intent: ["vérifier un avocat", "contrôler un avocat", "inscription Barreau", "avis avocat"],
    public_sources: [
      "Inscription au Barreau de l'avocat (registre public du CNB)",
      "Numéro CNBF de l'avocat",
      "Spécialisations certifiées (certificats de spécialisation)",
      "Date d'inscription et années d'exercice",
      "Cabinet d'exercice et adresse professionnelle déclarée",
    ],
    why_check: [
      "S'assurer que l'avocat est bien inscrit à un Barreau français en activité",
      "Vérifier sa spécialisation déclarée (droit du travail, fiscal, immobilier, etc.)",
      "Identifier son cabinet et son adresse professionnelle officielle",
      "Préparer votre dossier avant le premier rendez-vous",
    ],
    confilex_check: [
      "Référencement après qualification documentaire et vérification d'inscription",
      "Pas de classement, pas de notation publique, pas de commission CONFILEX",
      "Mise en relation après qualification de votre besoin",
      "Espace sécurisé pour transmettre vos pièces préparées",
    ],
    profession_slug: "avocat",
  },
  "verifier-un-expert-comptable": {
    label: "expert-comptable",
    plural: "experts-comptables",
    h1: "Vérifier un expert-comptable avant collaboration",
    seo_title: "Vérifier un expert-comptable — Contrôle CONFILEX",
    seo_description: "Avant de confier vos comptes à un expert-comptable, vérifiez son inscription à l'Ordre, sa spécialisation et son expérience. Contrôle institutionnel CONFILEX.",
    intent: ["vérifier un expert-comptable", "contrôler un expert-comptable", "ordre des experts-comptables", "avis expert-comptable"],
    public_sources: [
      "Inscription à l'Ordre des experts-comptables (CSOEC)",
      "Numéro d'inscription au tableau de l'Ordre",
      "Spécialisations déclarées (audit, paie, fiscalité, immobilier)",
      "Date d'inscription et années d'exercice",
      "Cabinet d'exercice et adresse professionnelle officielle",
    ],
    why_check: [
      "S'assurer que l'expert-comptable est inscrit à l'Ordre (CSOEC)",
      "Vérifier ses spécialisations (LMNP, SCI, audit, paie, fiscalité…)",
      "Confirmer l'adresse de son cabinet",
      "Préparer vos pièces comptables avant le premier rendez-vous",
    ],
    confilex_check: [
      "Référencement après qualification de l'inscription à l'Ordre",
      "Pas de classement, pas de notation, pas de commission CONFILEX sur les honoraires",
      "Mise en relation après qualification de votre activité (TPE, PME, holding, SCI…)",
      "Espace sécurisé pour transmettre vos pièces préparées",
    ],
    profession_slug: "expert-comptable",
  },
  "verifier-un-notaire": {
    label: "notaire",
    plural: "notaires",
    h1: "Vérifier un notaire avant signature d'un acte",
    seo_title: "Vérifier un notaire — Contrôle institutionnel · CONFILEX",
    seo_description: "Avant la signature d'un acte authentique, vérifiez l'identité de votre notaire (CSN), sa résidence et son habilitation. Contrôle CONFILEX.",
    intent: ["vérifier un notaire", "contrôler un notaire", "Conseil supérieur du notariat", "notaire compétent"],
    public_sources: [
      "Registre officiel du Conseil supérieur du notariat (CSN)",
      "Office notarial de rattachement",
      "Date d'installation et années d'exercice",
      "Spécialisations déclarées (immobilier, succession, transmission)",
    ],
    why_check: [
      "S'assurer que le notaire est en exercice et habilité",
      "Identifier son office notarial officiel",
      "Vérifier sa spécialité déclarée (succession, immobilier, donation…)",
      "Préparer vos documents avant le rendez-vous notarial",
    ],
    confilex_check: [
      "Référencement après vérification d'installation à un office notarial en exercice",
      "Pas de classement, pas de notation publique, pas de commission",
      "Orientation vers un notaire dont la spécialité correspond à votre acte",
      "Espace sécurisé pour transmettre vos pièces préparées",
    ],
    profession_slug: "notaire",
  },
  "verifier-un-commissaire-de-justice": {
    label: "commissaire de justice",
    plural: "commissaires de justice",
    h1: "Vérifier un commissaire de justice (ex-huissier)",
    seo_title: "Vérifier un commissaire de justice — CONFILEX",
    seo_description: "Vérifier l'identité d'un commissaire de justice : Chambre nationale, ressort de compétence, habilitation pour constats, recouvrement et exécution.",
    intent: ["vérifier un commissaire de justice", "vérifier un huissier", "Chambre nationale commissaires justice"],
    public_sources: [
      "Registre de la Chambre nationale des commissaires de justice (CNCJ)",
      "Ressort territorial de compétence",
      "Office d'exercice",
      "Date de prestation de serment",
    ],
    why_check: [
      "S'assurer que le commissaire est en exercice",
      "Identifier son ressort territorial pour vos actes",
      "Vérifier son office d'exercice",
    ],
    confilex_check: [
      "Référencement après vérification de l'inscription à la CNCJ",
      "Pas de classement, pas de commission",
      "Orientation par ressort territorial",
    ],
    profession_slug: "huissier",
  },
  "verifier-une-societe": {
    label: "société",
    plural: "sociétés",
    h1: "Vérifier une société (K-bis, dirigeants, statuts)",
    seo_title: "Vérifier une société — K-bis et documents · CONFILEX",
    seo_description: "Vérifier l'existence d'une société, identifier ses dirigeants, lire ses comptes annuels et son K-bis. Lecture documentaire CONFILEX.",
    intent: ["vérifier une société", "K-bis société", "vérifier entreprise", "vérifier dirigeant"],
    public_sources: [
      "Registre du Commerce et des Sociétés (RCS) — K-bis officiel",
      "INSEE / SIRENE pour le SIREN, SIRET, code NAF",
      "Bulletin officiel des annonces civiles et commerciales (BODACC)",
      "Comptes annuels déposés au greffe (Infogreffe)",
      "Tribunal de commerce — procédures collectives éventuelles",
    ],
    why_check: [
      "S'assurer de l'existence et de l'activité de la société",
      "Identifier les dirigeants officiels et leur date de nomination",
      "Lire les comptes annuels déposés au greffe",
      "Préparer un dossier exploitable avant tout engagement",
    ],
    confilex_check: [
      "Lecture documentaire factuelle d'un K-bis, statuts, comptes annuels",
      "Détection de divergences entre statuts et K-bis (forme sociale, capital, dirigeants)",
      "Audit transverse multi-pièces pour les dossiers complexes",
    ],
    profession_slug: null,
    cta_link: "/verifier-documents-entreprise",
  },
  "verifier-un-professionnel": {
    label: "professionnel",
    plural: "professionnels",
    h1: "Vérifier un professionnel avant de lui confier un dossier",
    seo_title: "Vérifier un professionnel du droit ou du chiffre · CONFILEX",
    seo_description: "Vérifier un avocat, un expert-comptable, un notaire, un commissaire de justice : inscription, spécialisation, expérience. Contrôle institutionnel CONFILEX.",
    intent: ["vérifier un professionnel", "contrôler un professionnel", "vérifier un spécialiste"],
    public_sources: [
      "Ordres professionnels (CNB pour les avocats, CSOEC pour les experts-comptables, CSN pour les notaires, CNCJ pour les commissaires de justice)",
      "Registres publics (Infogreffe, INSEE/SIRENE, BODACC)",
      "Bases publiques des spécialisations certifiées",
    ],
    why_check: [
      "Vérifier l'inscription à l'Ordre ou au Barreau",
      "Identifier sa spécialisation et ses années d'exercice",
      "Préparer un dossier exploitable avant le rendez-vous",
      "Gagner du temps lors du premier contact",
    ],
    confilex_check: [
      "Référencement après vérification institutionnelle",
      "Mise en relation après qualification documentaire de votre besoin",
      "Espace sécurisé pour transmettre vos pièces",
      "Aucune commission CONFILEX sur les honoraires des professionnels",
    ],
    profession_slug: null,
  },
};


export default function VerifyProfessionalPage({ slug }) {
  const cfg = VERIFY_PRO_PAGES[slug];
  if (!cfg) return <div className="p-20 text-center">Page non trouvée.</div>;

  const faq = [
    {
      q: `Comment puis-je vérifier qu'un ${cfg.label} est bien inscrit ?`,
      a: `Les ordres professionnels en France publient un registre officiel de leurs membres en exercice. CONFILEX référence des ${cfg.plural} après qualification documentaire de cette inscription. La mise en relation passe par une qualification préalable de votre besoin.`,
    },
    {
      q: `CONFILEX vérifie-t-il l'expérience et la qualité du ${cfg.label} ?`,
      a: `CONFILEX vérifie factuellement les éléments publics : inscription, ordre, ressort, spécialisations déclarées. CONFILEX ne note pas, ne classe pas et ne publie pas d'avis sur la qualité d'un professionnel. Aucune commission n'est perçue par CONFILEX sur les honoraires.`,
    },
    {
      q: `Que dois-je préparer avant le premier rendez-vous ?`,
      a: `Préparer vos pièces (contrat, courriers, K-bis, statuts, comptes annuels, mises en demeure, courriels échangés) accélère grandement la consultation. Le Vérificateur CONFILEX produit une lecture factuelle de ces pièces : résumé, dates clés, points à clarifier.`,
    },
    {
      q: `Comment CONFILEX me met-il en relation avec un ${cfg.label} ?`,
      a: `CONFILEX qualifie votre besoin via une orientation institutionnelle (gratuite ou Mission Premium 89 € TTC), puis vous propose des profils de ${cfg.plural} dont la spécialisation correspond. Vous restez libre du choix final et de la négociation des honoraires.`,
    },
    {
      q: `Y a-t-il un coût pour utiliser CONFILEX ?`,
      a: `L'orientation initiale est gratuite. Le Vérificateur (lecture documentaire) est facturé 29 à 49 € selon le palier choisi. La Mission de Recherche Premium est facturée 89 € TTC. CONFILEX ne facture aucun honoraire pour le compte des professionnels.`,
    },
  ];

  const Icon = slug === "verifier-une-societe" ? Building2 :
                slug === "verifier-un-notaire" ? Scale : BadgeCheck;

  const ctaLink = cfg.cta_link || (cfg.profession_slug
    ? `/trouver-un-${cfg.profession_slug === "huissier" ? "commissaire-de-justice"
        : cfg.profession_slug === "expert-comptable" ? "expert-comptable"
        : cfg.profession_slug}`
    : "/orientation-intelligente");

  return (
    <>
      <Seo
        title={cfg.seo_title}
        description={cfg.seo_description}
        canonical={`/${slug}`}
        keywords={cfg.intent}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": cfg.h1,
            "description": cfg.seo_description,
            "provider": { "@type": "Organization", "name": "CONFILEX", "url": "https://confilex.fr" },
            "areaServed": { "@type": "Country", "name": "France" },
            "serviceType": "Contrôle institutionnel d'un professionnel",
          },
          ldBreadcrumb([
            { name: "Accueil", url: "/" },
            { name: "Vérifier un professionnel", url: "/verifier-un-professionnel" },
            { name: cfg.h1, url: `/${slug}` },
          ]),
          ldFAQ(faq),
        ]}
      />

      <main className="bg-paper min-h-screen" data-testid={`verify-pro-${slug}`}>
        <section className="bg-night text-paper">
          <div className="max-w-5xl mx-auto px-6 py-16">
            <p className="text-[11px] tracking-[0.28em] uppercase text-copper mb-3 font-semibold inline-flex items-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5" /> CONFILEX · CONTRÔLE INSTITUTIONNEL
            </p>
            <h1 className="font-serif italic text-4xl sm:text-5xl lg:text-6xl leading-tight mb-5" data-testid="page-h1">
              {cfg.h1}
            </h1>
            <p className="text-paper/75 text-base max-w-3xl leading-relaxed mb-6">
              CONFILEX vous aide à vérifier l'inscription, la spécialisation et l'expérience
              déclarée des {cfg.plural} avant de leur confier votre dossier — puis à transmettre
              un dossier déjà préparé et exploitable.
            </p>
            <div className="flex gap-3 flex-wrap">
              <Link to={ctaLink}>
                <Button className="bg-copper text-paper hover:bg-copper/90 rounded-sm" data-testid="cta-find">
                  <Compass className="w-4 h-4 mr-2" /> Trouver un {cfg.label} qualifié
                </Button>
              </Link>
              <Link to="/verifier">
                <Button variant="outline" className="border-paper/40 text-paper hover:bg-paper/10 rounded-sm" data-testid="cta-verifier">
                  <ScanLine className="w-4 h-4 mr-2" /> Préparer mon dossier
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-6 py-12">
          <p className="text-[11px] tracking-[0.28em] uppercase text-copper mb-3 font-semibold">
            POURQUOI VÉRIFIER ?
          </p>
          <h2 className="font-serif italic text-3xl text-night mb-6">
            Les bonnes raisons de contrôler avant d'engager
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {cfg.why_check.map((reason, i) => (
              <Card key={i} className="p-4 border-sage hover:border-copper transition-smooth flex gap-3" data-testid={`reason-${i}`}>
                <Icon className="w-5 h-5 text-copper flex-shrink-0 mt-0.5" />
                <p className="text-sm text-night leading-relaxed">{reason}</p>
              </Card>
            ))}
          </div>
        </section>

        <section className="bg-ivory border-t border-sage">
          <div className="max-w-5xl mx-auto px-6 py-12">
            <p className="text-[11px] tracking-[0.28em] uppercase text-copper mb-3 font-semibold">SOURCES PUBLIQUES</p>
            <h2 className="font-serif italic text-3xl text-night mb-6">
              Les informations publiques disponibles
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {cfg.public_sources.map((src, i) => (
                <Card key={i} className="p-4 bg-paper border-sage" data-testid={`source-${i}`}>
                  <Search className="w-4 h-4 text-copper mb-2" />
                  <p className="text-sm text-night leading-snug">{src}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-6 py-12">
          <p className="text-[11px] tracking-[0.28em] uppercase text-copper mb-3 font-semibold">L'APPROCHE CONFILEX</p>
          <h2 className="font-serif italic text-3xl text-night mb-6">Ce que CONFILEX apporte</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {cfg.confilex_check.map((item, i) => (
              <Card key={i} className="p-4 border-copper/30 bg-paper flex gap-3" data-testid={`confilex-${i}`}>
                <FileCheck2 className="w-5 h-5 text-copper flex-shrink-0 mt-0.5" />
                <p className="text-sm text-night leading-relaxed">{item}</p>
              </Card>
            ))}
          </div>
        </section>

        <section className="max-w-3xl mx-auto px-6 py-12" data-testid="page-faq">
          <p className="text-[11px] tracking-[0.28em] uppercase text-copper mb-3 font-semibold">QUESTIONS FRÉQUENTES</p>
          <h2 className="font-serif italic text-3xl text-night mb-6">À savoir avant de vérifier</h2>
          <div className="space-y-4">
            {faq.map((item, i) => (
              <Card key={i} className="p-5 border-sage" data-testid={`faq-item-${i}`}>
                <h3 className="font-semibold text-night mb-2">{item.q}</h3>
                <p className="text-sm text-night/75 leading-relaxed">{item.a}</p>
              </Card>
            ))}
          </div>
        </section>

        <section className="bg-night text-paper">
          <div className="max-w-5xl mx-auto px-6 py-10">
            <p className="text-[11px] tracking-[0.28em] uppercase text-copper mb-3 font-semibold">VÉRIFIER UN AUTRE PROFESSIONNEL</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {Object.entries(VERIFY_PRO_PAGES)
                .filter(([s]) => s !== slug)
                .slice(0, 6)
                .map(([s, c]) => (
                  <Link key={s} to={`/${s}`} className="block p-3 border border-paper/20 rounded-sm hover:border-copper hover:bg-paper/5 transition-smooth" data-testid={`related-${s}`}>
                    <p className="text-sm font-medium">{c.h1.replace("Vérifier ", "")}</p>
                  </Link>
                ))}
            </div>
          </div>
        </section>

        <section className="border-t border-sage bg-paper">
          <div className="max-w-5xl mx-auto px-6 py-8 flex items-start gap-3 text-sm text-night/75">
            <Lock className="w-5 h-5 text-copper flex-shrink-0 mt-0.5" />
            <p>
              CONFILEX vérifie factuellement des éléments publics d'inscription et d'organisation
              professionnelle. CONFILEX ne note pas, ne classe pas et ne publie pas d'avis sur la
              qualité d'un professionnel. Les professionnels référencés exercent en toute
              indépendance et conservent l'entière responsabilité de leur analyse.
            </p>
          </div>
        </section>
      </main>
    </>
  );
}
